import type { AvailableActionOption, Game } from '../../types/game'
import { assetUrl } from '../../utils/assets'
import { Worker } from '../shared/Worker'
import { ResourceIcon } from '../shared/ResourceIcon'

interface EvolutionTreeProps {
  game: Game
  availableActions: AvailableActionOption[]
  onEvolve: (evolutionId: number) => void
}

export function EvolutionTree({ game, availableActions, onEvolve }: EvolutionTreeProps) {
  const evolvableIds = new Set(
    availableActions
      .filter((action) => action.action === 'evolve' && typeof action.target_id === 'number')
      .map((action) => action.target_id as number)
  )

  const currentPlayer = game.current_player
  const evolvedIds = new Set(currentPlayer.evolutions.map((e) => e.id))
  const columns = { 1: [], 2: [], 3: [], 4: [], 5: [] } as Record<number, JSX.Element[]>

  for (const evolution of game.evolutions) {
    let status: string | undefined
    if (evolvedIds.has(evolution.id)) {
      status = 'evolved'
    } else if (evolvableIds.has(evolution.id)) {
      status = 'available'
    } else if (evolution.level > game.clear_infections_unlock_level) {
      status = 'infection-unlocked'
    }

    const workers = Object.entries(game.evolution_tracker)
      .filter(([, evolutionId]) => evolutionId === evolution.id)
      .map(([color]) => <Worker key={`${evolution.id}-${color}`} color={color as keyof typeof game.evolution_tracker & any} showImage={false} />)

    columns[evolution.level].push(
      <div key={evolution.id} className="evolution">
        <div
          className={status ? `evolution-tile ${status}` : 'evolution-tile'}
          onClick={() => onEvolve(evolution.id)}
        >
          <div className="evolution-tile-image">
            <img
              src={assetUrl('images', 'evolutions', `${evolution.type}.png`)}
              alt={evolution.name}
              className="evolution-tile-image-img"
            />
          </div>
          <div className="evolution-tile-worker-container">{workers}</div>
        </div>
        <div className="evolution-description">
          <div className="evolution-description-header">{evolution.name}</div>
          <div className="evolution-description-body">{evolution.description}</div>
          <div className="evolution-description-effect">{evolution.effect}</div>
          <div className="flower-yield evolution-cost">
            {Object.entries(evolution.cost)
              .filter(([, amount]) => amount > 0)
              .map(([resourceOrBrood, amount]) => (
                <div key={`${evolution.id}-${resourceOrBrood}`} className="evolution-cost-pair">
                  <div>{amount}</div>
                  <ResourceIcon resource={resourceOrBrood as 'nectar' | 'pollen' | 'honey' | 'wax' | 'egg' | 'worker'} />
                </div>
              ))}
          </div>
        </div>
      </div>
    )
  }

  const placeholderColumns = Array.from({ length: 5 }, (_, index) => (
    <div key={`placeholder-${index}`} className="evolution-column">
      <div className="evolution placeholder">
        <div className="evolution-tile placeholder" />
      </div>
    </div>
  ))

  const workerPool = Object.keys(game.evolvers).map((color) => (
    <Worker key={`evolver-${color}`} color={color as any} />
  ))

  const trackerPool = game.players
    .filter((player) => !(player.color in game.evolution_tracker))
    .map((player) => <Worker key={`tracker-${player.color}`} color={player.color} showImage={false} />)

  const renderedColumns = Object.values(columns).map((column, index) => (
    <div key={`column-${index + 1}`} className="evolution-column">
      {column}
    </div>
  ))

  return (
    <div className="evolution-tree tableau">
      <div className="evolution-tree-container">
        <div className="evolution-tree-header">Evolve</div>
        <div id="evolution-worker-pool" className="flower-market-token-pool evolution-worker-pool" data-worker-target="evolve-pool">
          {workerPool}
        </div>
      </div>
      <div className="evolution-tree-container">
        <div className="evolutions-container">
          <div id="evolution-tracker-pool" className="flower-market-token-pool evolution-tracker-pool">
            {trackerPool}
          </div>
          <div id="evolutions" className="evolutions">
            {renderedColumns.some((column) => column.props.children.length > 0) ? renderedColumns : placeholderColumns}
          </div>
          <div id="evolution-description" />
        </div>
      </div>
    </div>
  )
}
